import { AuthTypes } from '~/auth/types/auth.types'

import { GemType } from '~/components/Rock/types'
import { ActivityQuestionType, ContentType } from '~/types/bootcamp'
import { IUser, UserType } from '~/types/user-info-types'

export interface PageRequest {
  page: number
  range: number
  search?: string
}

export interface CountryCount {
  country: string
  count: number
}

export interface UserTypeCount {
  userType: UserType
  count: number
}

export interface UserRegistration {
  birthYear: string
  country: string
  email?: string
  nickname: string
  password?: string
  passwordConfirm?: string
  wantsNewsletter: boolean
}

interface Error {
  status: number
  type: 'error'
  data: {
    type: 'error'
    message: string
    errorCode: string
  }
}

interface Success<T> {
  type: 'success'
  userData?: IUser
  data: T
}

export interface ShipState {
  shipPosX: number
  shipPosZ: number
  shipRotation: number
}
export interface CraneGameLevelData {
  levelName: string
  boxesDestroyed: number
  boxesLifted: number
  date: number
  specialBoxCount: number
  goldBoxesGotten: boolean[]
}

export interface GameRewards {
  xp: number
  kudos: number
  gemRed: number
  gemGreen: number
  gemBlue: number
}

export interface MultGameRewards {
  awardPoints: number
  gemType: GemType
  gemCount: number
}

export type Result<T, S extends boolean = false> = S extends true ? Success<T> : Success<T> | Error

export type SuccessResult<T> = Success<T>

export type ApiResponse<T> = T | Error

export interface DoesUserExistResponse {
  userDoesExist: boolean
  simpleUserProfile?: {
    avatar: string
    nickname: string
  }
  type?: 'email' | 'facebook'
}

export interface AuthenticationResponse {
  accessToken: string
  firebaseToken: string
  user: IUser
  log: boolean
}

export interface UserExistResponse {
  type: AuthTypes
  simpleUserProfile: { avatar: string; nickname: string; userHasPassword: boolean; email: string }
}

export interface RefreshTokenResponse {
  success: boolean
  accessToken: string
  firebaseToken: string
}

export interface FirebaseCustomTokenResponse {
  success: boolean
  firebaseToken: string
}

export enum ActivityCategory {
  BOOTCAMP = 'bootcamp',
  MATH_ESSENTIALS = 'mathEssentials',
  MATH_VAULT = 'mathVault',
  BOOTCAMP_VIDEO = 'bootcampVideo',
  CURRICULUM_VIDEO = 'curriculumVideo',
}

export enum GenerationMethod {
  DYNAMIC = 'dynamic',
  STATIC = 'static',
}

interface BootcampMetadata {
  type: ActivityQuestionType
  category: string
  subject: string
  bootcampNumber: number
  pageLevelNumber: number
  activityType: number
  variationKey?: string
  questionId?: string
  interactiveActivityName?: string
}

interface BootcampVideoMetadata {
  type: ContentType
  category: string
  subject: string
  bootcampNumber: number
  pageLevelNumber: number
  activityType: number
}

interface MathVaultMetadata {
  elevator: string
  room: string
  chest: string
  tab: number
  activityNumber: number
}

interface MathEssentialMetadata {
  topicNumber: number
  activityNumber: number
  questionNumber: number
}

interface CurriculumVideoMetadata {
  type: ContentType
  countryCode: string
  grade: number
  gradeTitle: string
  topic: string
  topicOrder: number
  subTopic: string
  subTopicOrder: number
  levelIndexOrder: number
  contentId: string
  videoId: string
}

export type ActivityMetadata =
  | BootcampMetadata
  | MathVaultMetadata
  | MathEssentialMetadata
  | BootcampVideoMetadata
  | CurriculumVideoMetadata

export interface StripeProduct {
  id: string
  name: string
  prices: StripePrice[]
}

export interface StripePrice {
  id: string
  currency: string
  unit_amount: number
  recurring: StripePriceRecurring
  tiers?: Array<{ unit_amount: number }>
}
export interface StripeProductGeolocation {
  stripeProductId: string
  stripePriceId: string
  country: string
  trialPeriodDays: number
  isDefault: boolean
}

export interface StripePriceRecurring {
  interval: string
  interval_count: number
  trial_period_days?: number
}

export interface SubscriptionPermission {
  stripeProductId: string
  feature: string
}
export interface FeatureFreeAccessDuration {
  feature: string
  duration: number
  timerBased: boolean
}

export interface FeatureFreeAccessDurationEdit {
  id: number
  duration: number
  timerBased: boolean
}

export interface CheckListItemType {
  isCompleted: boolean
  isVisible: boolean
  xp?: number
  gems?: number
}
export interface CheckListTypes {
  [itemKey: string]: CheckListItemType
}

export interface UserFeedbackEmojiPropsType {
  active?: boolean
  extended?: boolean
}

export interface RewardsEventIds {
  eventId: string
}

export interface TimeTrackingViewKeyRule {
  id: number
  viewKey: string
  label: string
  isExcluded: boolean
  updatedBy: number | null
  updatedAt: string
}
